/** STORY-113 part 2 — the identity a student's own device holds.
 *
 * The durable identifier is a keypair this device generates, never a row a teacher creates:
 * two teachers who never sync still record the same `student_key`, so the cloud merges by key
 * instead of guessing (docs/DESIGN-STUDENT-IDENTITY.md).
 *
 * The wire format is the session service's, verbatim — `student_keys.py` there is the other
 * half of this file and the two must not drift:
 *
 * - `student_key` is unpadded **base64url of SHA-256 over the raw 32-byte public key**, 43
 *   characters, no prefix;
 * - `public_key` is **standard base64** (not base64url) of the raw key;
 * - the signed message is `signup|<session_id>|<student_key>|<challenge>`, UTF-8, and the
 *   challenge is single-use and comes from the server;
 * - the signature is standard base64 of the 64 raw bytes.
 *
 * ## What is stored, and where
 *
 * - the **private key** as a `CryptoKey` in IndexedDB, always `extractable: false`. It is
 *   never in localStorage, never a string, and `crypto.subtle.exportKey` on it is refused by
 *   the engine — an XSS on this app can ask it to sign, but cannot carry it away.
 * - the **card** (`student_key`, display name, public key) in localStorage, so a screen can
 *   read it without awaiting IndexedDB. All of it is public by design.
 * - the **sealed blob**, only when the student set a recovery passphrase: the private key
 *   wrapped with AES-GCM under a PBKDF2 key derived from it. The passphrase is never stored,
 *   so the blob is inert without the student.
 *
 * ## Why the key is non-extractable and transfer still works
 *
 * `wrapKey` needs an extractable key, and STORY-113 needs a QR that carries the private key to
 * a second device. Both hold because the wrap happens **once, at sign-up**: the pair is
 * generated extractable, wrapped under the passphrase in the same function, and the private
 * key is then re-imported `extractable: false` while the extractable handle is dropped. From
 * that moment nothing in the app can read the key material; the QR re-renders the blob that
 * was sealed at birth, and the importing device seals its copy the same way. A student who
 * sets no passphrase gets a key that is non-extractable from generation and simply cannot be
 * moved — which the sign-up screen says out loud, because a silent single point of failure is
 * worse than a stated one.
 *
 * ## Where Ed25519 is missing
 *
 * WebCrypto Ed25519 is recent (Chrome 137, Safari 17, Firefox 129) and the session service
 * verifies **nothing else**. So there is no fallback curve worth choosing: a P-256 key would
 * be minted, sent, and refused as `bad_key`, and the student would be told their sign-up
 * failed for no reason they could act on. `createIdentity` refuses up front with `no_ed25519`
 * and the screen says so. Degrading loudly beats degrading to something the other side will
 * not accept.
 */
import { serviceUrl } from "../api/base";
import { storageKey } from "../product";

export type Signups = "off" | "open" | "request";
/** The only algorithm either side accepts; kept as a field so a v2 blob can say otherwise. */
export type Alg = "Ed25519";
export type RegistrationStatus = "none" | "pending" | "active" | "declined";

/** Everything about this student that is safe to show, print in a QR or send to a teacher. */
export type StudentCard = {
  /** 43 chars, base64url, no prefix — the fingerprint the teacher's system files them under. */
  student_key: string;
  display_name: string;
  alg: Alg;
  /** Raw public key, standard base64: what `RegistrationIn.public_key` wants. */
  public_key: string;
  created_at: string;
  /** Can this device hand the key to another one (a passphrase was set at sign-up)? */
  transferable: boolean;
};

/** One teacher system's answer about this key, as that system last gave it. */
export type Registration = {
  student_key: string;
  session_id: string;
  course_id: string | null;
  status: Exclude<RegistrationStatus, "none">;
  course_title: string | null;
  email_conflict: boolean;
  registered_at: string;
};

export class SignupError extends Error {
  constructor(readonly code: string) {
    super(code);
    this.name = "SignupError";
  }
}

const CARD = storageKey("identity.card");
const BLOB = storageKey("identity.transfer");
const REGS = storageKey("identity.registrations");
const POLICY = storageKey("identity.signups");
const DB = storageKey("identity");

// --- codecs ----------------------------------------------------------------------------

const b64 = (buf: ArrayBuffer): string => {
  let s = "";
  for (const b of new Uint8Array(buf)) s += String.fromCharCode(b);
  return btoa(s);
};

const b64u = (buf: ArrayBuffer): string => b64(buf).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

const unb64 = (text: string): Uint8Array => {
  const t = text.replace(/-/g, "+").replace(/_/g, "/");
  const s = atob(t + "=".repeat((4 - (t.length % 4)) % 4));
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
};

const bytes = (u: Uint8Array): ArrayBuffer => u.buffer.slice(u.byteOffset, u.byteOffset + u.byteLength) as ArrayBuffer;

const utf8 = (text: string): ArrayBuffer => bytes(new TextEncoder().encode(text));

// --- the algorithm, and the two shapes of an Ed25519 public key -------------------------

const KEY_ALG: AlgorithmIdentifier = "Ed25519";

/** SPKI is the same 32 bytes behind a fixed 12-byte DER header. Both are handled here rather
 * than relying on `importKey("raw")`, which not every engine offers for Ed25519. */
const SPKI_PREFIX = new Uint8Array([0x30, 0x2a, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x03, 0x21, 0x00]);

function rawOf(spki: ArrayBuffer): ArrayBuffer {
  const u = new Uint8Array(spki);
  if (u.length === 32) return spki;
  if (u.length === SPKI_PREFIX.length + 32 && SPKI_PREFIX.every((b, i) => u[i] === b)) return bytes(u.slice(SPKI_PREFIX.length));
  throw new SignupError("bad_key");
}

function spkiOf(raw: Uint8Array): ArrayBuffer {
  const out = new Uint8Array(SPKI_PREFIX.length + raw.length);
  out.set(SPKI_PREFIX);
  out.set(raw, SPKI_PREFIX.length);
  return bytes(out);
}

async function generate(extractable: boolean): Promise<CryptoKeyPair> {
  try {
    return (await crypto.subtle.generateKey("Ed25519", extractable, ["sign", "verify"])) as CryptoKeyPair;
  } catch {
    throw new SignupError("no_ed25519"); // see the header: there is no second curve to try
  }
}

/** `student_key`, exactly as `student_keys.fingerprint` computes it on the other side. */
const fingerprint = async (raw: ArrayBuffer): Promise<string> => b64u(await crypto.subtle.digest("SHA-256", raw));

// --- the private key in IndexedDB -------------------------------------------------------

type Held = { id: "self"; priv: CryptoKey; pub: CryptoKey };

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore("keys", { keyPath: "id" });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("indexeddb"));
  });
}

function work<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const req = run(db.transaction("keys", mode).objectStore("keys"));
        req.onsuccess = () => resolve(req.result as T);
        req.onerror = () => reject(req.error ?? new Error("indexeddb"));
      }),
  );
}

/** CryptoKey survives structured cloning with its non-extractability intact, which is the
 * whole reason the key lives here and not in localStorage. */
const hold = (h: Held): Promise<unknown> => work("readwrite", (s) => s.put(h));
const held = (): Promise<Held | undefined> => work<Held | undefined>("readonly", (s) => s.get("self"));

// --- the card, registrations and the course policy (synchronous, all public) ------------

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* a locked-down browser: the identity lasts as long as the tab does */
  }
}

export const card = (): StudentCard | null => read<StudentCard | null>(CARD, null);

export const registrations = (): Registration[] => read<Registration[]>(REGS, []);

export const registrationFor = (sessionId: string): Registration | null =>
  registrations().find((r) => r.session_id === sessionId) ?? null;

/** Signed up means a teacher system said **active**. A `request` course answers `pending`, and
 * the story is explicit that such a student stays a guest until the teacher approves — so the
 * bottom navigation stays away and the join screen keeps its single job. */
export const isSignedUp = (): boolean => !!card() && registrations().some((r) => r.status === "active");

export function rememberRegistration(r: Registration): void {
  write(REGS, [...registrations().filter((x) => x.session_id !== r.session_id), r]);
}

/** Narrow whatever the server sent to a policy this app knows, or nothing. */
export const asSignups = (v: unknown): Signups | null => (v === "off" || v === "open" || v === "request" ? v : null);

const asStatus = (v: unknown): RegistrationStatus => (v === "pending" || v === "active" || v === "declined" ? v : "none");

/** The course's sign-up policy, as the teacher's system last told this device. Defaults to
 * `off`: a policy nobody stated is a policy that shows no footnote. */
export const signupsOf = (sessionId: string): Signups => read<Record<string, Signups>>(POLICY, {})[sessionId] ?? "off";

/** The most recent policy this device saw, for the join screen, which asks before it has a
 * session to ask about. */
export const lastSignups = (): Signups => read<Record<string, Signups>>(POLICY, {})["last"] ?? "off";

export function rememberSignups(sessionId: string, policy: unknown): void {
  const p = asSignups(policy);
  if (!p) return;
  write(POLICY, { ...read<Record<string, Signups>>(POLICY, {}), [sessionId]: p, last: p });
}

// --- making, and moving, the key ---------------------------------------------------------

type Sealed = { v: 1; k: string; n: string; alg: Alg; pk: string; kdf: { s: string; i: number }; iv: string; ct: string };

const ITERATIONS = 250_000;

async function passphraseKey(passphrase: string, salt: Uint8Array, iterations: number, use: "wrapKey" | "unwrapKey"): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey("raw", utf8(passphrase.normalize("NFKC")), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: bytes(salt), iterations, hash: "SHA-256" },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    [use],
  );
}

/** Mint this device's identity. With a passphrase the key is sealed for transfer in the same
 * breath and then re-imported non-extractable; without one it is never extractable at all. */
export async function createIdentity(displayName: string, passphrase?: string): Promise<StudentCard> {
  const wants = !!passphrase && passphrase.length > 0;
  const pair = await generate(wants);
  const spki = await crypto.subtle.exportKey("spki", pair.publicKey);
  const raw = rawOf(spki);
  const student_key = await fingerprint(raw);
  let priv = pair.privateKey;

  if (wants) {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const wrapper = await passphraseKey(passphrase, salt, ITERATIONS, "wrapKey");
    const ct = await crypto.subtle.wrapKey("pkcs8", pair.privateKey, wrapper, { name: "AES-GCM", iv: bytes(iv) });
    const sealed: Sealed = {
      v: 1,
      k: student_key,
      n: displayName,
      alg: "Ed25519",
      pk: b64u(raw),
      kdf: { s: b64u(bytes(salt)), i: ITERATIONS },
      iv: b64u(bytes(iv)),
      ct: b64u(ct),
    };
    write(BLOB, sealed);
    // and now throw the extractable handle away: re-import the same key, sealed.
    const pkcs8 = await crypto.subtle.exportKey("pkcs8", pair.privateKey);
    priv = await crypto.subtle.importKey("pkcs8", pkcs8, KEY_ALG, false, ["sign"]);
  }

  await hold({ id: "self", priv, pub: pair.publicKey });
  const made: StudentCard = {
    student_key,
    display_name: displayName,
    alg: "Ed25519",
    public_key: b64(raw),
    created_at: new Date().toISOString(),
    transferable: wants,
  };
  write(CARD, made);
  return made;
}

export function renameIdentity(displayName: string): void {
  const c = card();
  if (c) write(CARD, { ...c, display_name: displayName });
}

/** Sign the message the session service will rebuild and check. Standard base64, its shape. */
export async function sign(message: string): Promise<string> {
  const h = await held();
  if (!h) throw new SignupError("no_key");
  return b64(await crypto.subtle.sign(KEY_ALG, h.priv, utf8(message)));
}

/** The QR the old device shows: the student card plus the private key under the passphrase.
 * Null when this identity was minted without one and therefore cannot travel. */
export const transferPayload = (): string | null => {
  const sealed = read<Sealed | null>(BLOB, null);
  return sealed ? JSON.stringify(sealed) : null;
};

/** Adopt an identity from another device. The passphrase is the only thing that can open it:
 * AES-GCM fails closed, so a wrong one throws here and never yields a half-usable key. */
export async function importTransfer(payload: string, passphrase: string): Promise<StudentCard> {
  let sealed: Sealed;
  try {
    sealed = JSON.parse(payload.trim()) as Sealed;
  } catch {
    throw new SignupError("not_a_code");
  }
  if (sealed.v !== 1 || !sealed.ct || !sealed.pk || !sealed.k) throw new SignupError("not_a_code");
  const raw = unb64(sealed.pk);
  if ((await fingerprint(bytes(raw))) !== sealed.k) throw new SignupError("bad_key");

  const wrapper = await passphraseKey(passphrase, unb64(sealed.kdf.s), sealed.kdf.i, "unwrapKey");
  const priv = await crypto.subtle.unwrapKey(
    "pkcs8",
    bytes(unb64(sealed.ct)),
    wrapper,
    { name: "AES-GCM", iv: bytes(unb64(sealed.iv)) },
    KEY_ALG,
    false, // sealed again on this device; the QR, not the key, is what travels onward
    ["sign"],
  );
  const pub = await crypto.subtle.importKey("spki", spkiOf(raw), KEY_ALG, true, ["verify"]);
  // the pair must actually belong together, or this device would sign as a key nobody knows
  const probe = utf8(`transfer|${sealed.k}`);
  const ok = await crypto.subtle.verify(KEY_ALG, pub, await crypto.subtle.sign(KEY_ALG, priv, probe), probe);
  if (!ok) throw new SignupError("bad_key");

  await hold({ id: "self", priv, pub });
  write(BLOB, sealed); // kept verbatim so this device can pass the identity on in turn
  const adopted: StudentCard = {
    student_key: sealed.k,
    display_name: sealed.n,
    alg: "Ed25519",
    public_key: b64(bytes(raw)),
    created_at: new Date().toISOString(),
    transferable: true,
  };
  write(CARD, adopted);
  write(REGS, []); // memberships belong to the teacher systems, not to the QR
  return adopted;
}

export async function forgetIdentity(): Promise<void> {
  write(CARD, null);
  write(BLOB, null);
  write(REGS, null);
  try {
    await work("readwrite", (s) => s.delete("self"));
  } catch {
    /* nothing held */
  }
}

// --- talking to the teacher's system -----------------------------------------------------

const at = (sessionId: string, suffix: string, token: string): string =>
  `${serviceUrl(`/session/v1/sessions/${sessionId}/register${suffix}`)}?token=${encodeURIComponent(token)}`;

const body = async (res: Response): Promise<Record<string, unknown> | null> =>
  (await res.json().catch(() => null)) as Record<string, unknown> | null;

const refuse = (res: Response, b: Record<string, unknown> | null): SignupError =>
  new SignupError(typeof b?.code === "string" ? b.code : res.status === 403 ? "signups_off" : "generic");

export type RegisterResult = { status: Exclude<RegistrationStatus, "none">; email_conflict: boolean };

/** Register this device's key with the teacher's system for one session.
 *
 * `open` answers `active` at once; `request` answers `pending` and the student stays a guest
 * until their teacher approves; `off` refuses with 403 `signups_off`. The challenge is spent
 * whatever happens next, so a failed attempt needs a fresh one — which is why the challenge is
 * fetched here rather than held.
 */
export async function register(
  sessionId: string,
  token: string,
  input: { display_name: string; email?: string; passphrase?: string },
): Promise<RegisterResult> {
  const issued = await fetch(at(sessionId, "/challenge", token), { method: "POST" });
  const challenge = await body(issued);
  if (!issued.ok) throw refuse(issued, challenge);
  if (typeof challenge?.challenge !== "string") throw new SignupError("generic");
  rememberSignups(sessionId, challenge.signups);

  const existing = card();
  const mine = existing ?? (await createIdentity(input.display_name, input.passphrase));
  if (existing && existing.display_name !== input.display_name) renameIdentity(input.display_name);

  // exactly what student_keys.signup_message builds on the other side
  const signature = await sign(`signup|${sessionId}|${mine.student_key}|${challenge.challenge}`);
  const res = await fetch(at(sessionId, "", token), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      student_key: mine.student_key,
      public_key: mine.public_key,
      display_name: input.display_name,
      email: input.email || undefined,
      challenge: challenge.challenge,
      signature,
    }),
  });
  const out = await body(res);
  if (!res.ok) throw refuse(res, out);
  const status = asStatus(out?.status);
  if (status === "none") throw new SignupError("generic");
  const conflict = out?.email_conflict === true;
  rememberRegistration({
    student_key: mine.student_key,
    session_id: sessionId,
    course_id: typeof out?.course_id === "string" ? out.course_id : null,
    status,
    course_title: null, // the service answers with ids; the title comes from the mastery read
    email_conflict: conflict,
    registered_at: new Date().toISOString(),
  });
  return { status, email_conflict: conflict };
}

/** Write down what a teacher system just said about this device, whatever told us: the join
 * response carries `signups` and `registration_status`, and the sign-up status endpoint says
 * the same two things later. `none` erases the local record — a teacher who declined or
 * removed a sign-up must not leave the student's app claiming otherwise. */
export function noteRegistration(
  sessionId: string,
  signups: unknown,
  status: unknown,
  extra: { student_key?: unknown; course_id?: unknown } = {},
): RegistrationStatus {
  rememberSignups(sessionId, signups);
  const s = asStatus(status);
  const key = typeof extra.student_key === "string" ? extra.student_key : card()?.student_key;
  if (s === "none" || !key) {
    if (s === "none") {
      write(
        REGS,
        registrations().filter((r) => r.session_id !== sessionId),
      );
    }
    return s;
  }
  const prior = registrationFor(sessionId);
  rememberRegistration({
    student_key: key,
    session_id: sessionId,
    course_id: typeof extra.course_id === "string" ? extra.course_id : (prior?.course_id ?? null),
    status: s,
    course_title: prior?.course_title ?? null,
    email_conflict: prior?.email_conflict ?? false,
    registered_at: prior?.registered_at ?? new Date().toISOString(),
  });
  return s;
}

/** Ask the teacher's system what it holds for THIS device, and nothing about anybody else.
 * The one way an approval or a decline reaches the student without a fresh sign-up. */
export async function refreshRegistration(sessionId: string, token: string): Promise<RegistrationStatus> {
  const res = await fetch(at(sessionId, "", token));
  const out = await body(res);
  if (!res.ok) return registrationFor(sessionId)?.status ?? "none"; // offline: keep what we know
  return noteRegistration(sessionId, out?.signups, out?.status, {
    student_key: out?.student_key,
    course_id: out?.course_id,
  });
}
